import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useIsMobile } from "../../hooks/use-mobile";
import { useReportIds } from "../../hooks/useReportIds";
import Loading from "../Loading";
import { motion } from "framer-motion";
import { useUser } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";

interface AssessmentData {
  reportType: string;
  userInfo: {
    callNumber: number;
    date: string;
    name: string;
    email: string;
  };
  concerns: string[];
  strengths: string[];
  motivations: string[];
  observations: string[];
  insights: string[];
  nextSteps: {
    explorationQuestions: string[];
  };
}


const InitialAssessmentReport = () => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { reportIds, isLoading } = useReportIds();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const location = useLocation();
  const [currentReportId, setCurrentReportId] = useState<string | null>(reportId || null);
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const user = useUser();
  
  // Check if we're on the start-report path (no specific report ID)
  const isStartReportPath = location.pathname.includes('/start-report/');
  
  useEffect(() => {
    if (isStartReportPath) {
      // For start-report path, use the INTAKE1 report ID if available
      if (reportIds.INTAKE1) {
        setCurrentReportId(reportIds.INTAKE1 as string);
      }
    } else if (!reportId && reportIds.INTAKE1) {
      // For other paths, use the reportId from params or fall back to INTAKE1
      setCurrentReportId(reportIds.INTAKE1 as string);
    } else if (reportId) {
      // If reportId is in URL, use it
      setCurrentReportId(reportId);
    }
  }, [reportId, reportIds.INTAKE1, isStartReportPath]);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!currentReportId) return;
      try {
        setLoading(true);
        const response = await fetch(`${(import.meta as any).env.VITE_APP_URL}/report/intake/1/${currentReportId}`, {
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.status === 500) {
          setError({ status: 500 });
          setLoading(false);
          return;
        }

        if (!response.ok) {
          setError({ status: response.status });
          setLoading(false);
          return;
        }

        const data = await response.json();
        setAssessmentData(data);
        setError(null);
      } catch (err: any) {
        setError({ status: 'error', message: err.message });
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [currentReportId]);

  if (isLoading || loading) {
    return <Loading className="h-screen" />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Error Loading Report</h1>
          <p className="text-gray-600 mb-6">
            {error.status === 404
              ? "Report not found. It may have been deleted or you don't have access."
              : error.status === 500
              ? "Server error. Please try again later."
              : `Error: ${error.message || "Unknown error"}`}
          </p>
          <Link to="/">
            <Button>Return to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

    // Format date for display
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'it' ? 'it-IT' : 'uk-UA', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };
  

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6">
      <div className="container max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
          <Link to="/">
            <Button variant="ghost" className="-ml-2 pl-2 flex items-center gap-2 text-base">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t("intakeProcess.backToIntake", "Back to Intake Process")}</span>
              <span className="sm:hidden">{t("navigation.back")}</span>
            </Button>
          </Link>
        </div>
        {/* Header */}
        <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold">{t("intakeProcess.session.initialAssessment.title")}</h1>
            <p className="mt-2">{formatDate(assessmentData?.userInfo?.date || '')} • {t("intakeProcess.session.number", "Session {{number}}", { number: assessmentData?.userInfo?.callNumber })}</p>
          <p className="text-gray-600">
            {assessmentData?.userInfo?.name} • {assessmentData?.userInfo?.email}
          </p>
        </div>
        
        
        {/* Concerns Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <Card className="overflow-hidden shadow-sm">
            <CardHeader className={`${isMobile ? "px-4 py-5" : ""}`}>
              <CardTitle>{t("intakeReport.sections.concerns", "Concerns")}</CardTitle>
            </CardHeader>
            
            <CardContent className={isMobile ? "px-4 pb-5 pt-4" : ""}>
              <ul className="list-disc pl-5 space-y-2">
                {assessmentData?.concerns.map((concern, index) => (
                  <li key={index} className="text-gray-700">{concern}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Strengths Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <Card className="overflow-hidden shadow-sm">
            <CardHeader className={`${isMobile ? "px-4 py-5" : ""}`}>
              <CardTitle>{t("intakeReport.sections.strengths", "Strengths")}</CardTitle>
            </CardHeader>
            
            <CardContent className={isMobile ? "px-4 pb-5 pt-4" : ""}>
              <ul className="list-disc pl-5 space-y-2">
                {assessmentData?.strengths.map((strength, index) => (
                  <li key={index} className="text-gray-700">{strength}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Motivations Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <Card className="overflow-hidden shadow-sm">
            <CardHeader className={`${isMobile ? "px-4 py-5" : ""}`}>
              <CardTitle>{t("intakeReport.sections.motivations", "Motivations")}</CardTitle>
            </CardHeader>
            
            <CardContent className={isMobile ? "px-4 pb-5 pt-4" : ""}>
              <ul className="list-disc pl-5 space-y-2">
                {assessmentData?.motivations.map((motivation, index) => (
                  <li key={index} className="text-gray-700">{motivation}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Observations Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          transition={{ delay: 0.4 }}
          className="mb-6"
        >
          <Card className="overflow-hidden shadow-sm">
            <CardHeader className={`${isMobile ? "px-4 py-5" : ""}`}>
              <CardTitle>{t("intakeReport.details.observations")}</CardTitle>
            </CardHeader>
            
            <CardContent className={isMobile ? "px-4 pb-5 pt-4" : ""}>
              <ul className="list-disc pl-5 space-y-2">
                {assessmentData?.observations.map((observation, index) => (
                  <li key={index} className="text-gray-700">{observation}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Insights Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          transition={{ delay: 0.5 }}
          className="mb-6"
        >
          <Card className="overflow-hidden shadow-sm">
            <CardHeader className={`${isMobile ? "px-4 py-5" : ""}`}>
              <CardTitle>{t("intakeReport.sections.insights", "Insights")}</CardTitle>
            </CardHeader>
            
            <CardContent className={isMobile ? "px-4 pb-5 pt-4" : ""}>
              <ul className="list-disc pl-5 space-y-2">
                {assessmentData?.insights.map((insight, index) => (
                  <li key={index} className="text-gray-700">{insight}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Next Steps Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={cardVariants}
          transition={{ delay: 0.6 }}
          className="mb-6"
        >
          <Card className="overflow-hidden shadow-sm">
            <CardHeader className={`${isMobile ? "px-4 py-5" : ""}`}>
              <CardTitle>{t("intakeReport.sections.nextSteps", "Next Steps")}</CardTitle>
            </CardHeader>
            
            <CardContent className={isMobile ? "px-4 pb-5 pt-4" : ""}>
              <h3 className="text-gray-700 font-medium mb-2">{t("intakeReport.nextSteps.explorationQuestions", "Exploration Questions for Next Session")}</h3>
              <ul className="list-disc pl-5 space-y-2">
                {assessmentData?.nextSteps.explorationQuestions.map((question, index) => (
                  <li key={index} className="text-gray-700">{question}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Disclaimer */}
        <div className="text-sm text-gray-500 mt-8 text-center">
          <p>{t("intakeReport.disclaimer")}</p>
        </div>
        
        {/* Continue Button */}
        <div className="mt-8 flex justify-center">
          <Link to={
            reportIds?.INTAKE2 || reportIds?.INTAKE ?
           reportIds?.INTAKE ? 
            `/auth/report/${reportIds.INTAKE2}/intake2` 
            : '/auth/start-report/intake2' 
            : '/auth/start-report/#health-assessment'
            }>
            <Button className="bg-primary hover:bg-primary/90">
              {reportIds?.INTAKE2 
                ? t("intakeProcess.buttons.viewNextReport", "View Health Assessment Report") 
                : t("intakeProcess.buttons.continueToNextSession", "Continue to Health Assessment Session")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default InitialAssessmentReport;